"use client";

import { Session } from "next-auth";
import { useSession } from "next-auth/react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

type AuthContextType = {
	user: Session["user"] | null;
	isLoading: boolean;
	isLoggedIn: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	updateSession: (data?: any) => Promise<Session | null>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const { data, status, update: updateSession } = useSession();
	const isLoading = status === "loading";
	const isLoggedIn = status === "authenticated";

	const [user, setUser] = useState<Session["user"] | null>(null);

	const handleSetUserData = useCallback(() => {
		try {
			if (!isLoading && data?.user) {
				setUser(data?.user);
			} else {
				setUser(null);
			}
		} catch (error) {
			console.log("Error occured while fetching user data, error: ", error);
		}
	}, [data?.user, isLoading]);

	useEffect(() => {
		handleSetUserData();
	}, [handleSetUserData]);

	return (
		<AuthContext.Provider value={{ user, isLoading, isLoggedIn, updateSession }}>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within a AuthProvider");
	}

	return context;
};
